import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import SoftBox from "components/SoftBox";
import SoftBadge from "components/SoftBadge";
import SoftTypography from "components/SoftTypography";
import prompts from "./components/prompttexts";
import PropTypes from 'prop-types';
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
//import getGPTResponse from "./components/openairesponse";
import { getFirestore, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import DeleteIcon from '@mui/icons-material/Delete'; // For the delete icon
import EditIcon from '@mui/icons-material/Edit'; // For the edit icon
import IconButton from '@mui/material/IconButton';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { Tooltip } from "@mui/material";
import CustomTooltip from "./customTooltip";
import Swal from "sweetalert2";


function PromptSettings({ promptsData, onUpdatePromptsData, refetchData, subscription }) {

  const [editingPromptId, setEditingPromptId] = useState(null); // Tracks the ID of the prompt being edited
  const [currentPromptId, setCurrentPromptId] = useState(null); // State for current prompt ID
  const [editedText, setEditedText] = useState('');
  const [selectedPrompts, setSelectedPrompts] = useState({});
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const toggleHelp = () => {
    setShowHelp(!showHelp);
  };

  useEffect(() => {
    // Initialize selectedPrompts state based on promptsData
    const initialSelectedPrompts = Object.keys(promptsData).reduce((acc, prompt) => ({ ...acc, [prompt]: false }), {});
    // setSelectedPrompts(initialSelectedPrompts);

    // Check for saved selected prompts in local storage
    const savedPrompts = JSON.parse(localStorage.getItem('selectedPrompts'));
    if (savedPrompts) {

      // Ensure all prompts in promptsData have a value in savedPrompts
      const updatedSavedPrompts = { ...initialSelectedPrompts, ...savedPrompts };
      setSelectedPrompts(updatedSavedPrompts);
    } else {
      setSelectedPrompts(initialSelectedPrompts);
    }
  }, [promptsData]);


  const [anchorEl, setAnchorEl] = useState(null); // State for menu anchor

  const handleSwitchChange = (prompt) => {
    const newSelectedPrompts = { ...selectedPrompts, [prompt]: !selectedPrompts[prompt] };
    setSelectedPrompts(newSelectedPrompts);
    localStorage.setItem('selectedPrompts', JSON.stringify(newSelectedPrompts));
  };

  const handleMenuClick = (event, promptId) => {
    setAnchorEl({ [promptId]: event.currentTarget });
    setCurrentPromptId(promptId);
    //console.log("##, ", promptId)
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    const savedPrompts = JSON.parse(localStorage.getItem('selectedPrompts'));
    if (savedPrompts) {
      setSelectedPrompts(savedPrompts);
    }
  }, []);



  // Editing of Prompts 

  const handleEditClick = () => {
    handleMenuClose(); // Close the menu
    setEditingPromptId(currentPromptId);
    setEditedText(promptsData[currentPromptId]);
  };

  const handleCloseEdit = () => {
    setEditingPromptId(null);
    setEditedText('');
  }
  
  const handleTextChange = (event) => {
    setEditedText(event.target.value);
  };
  
  const handleSave = async () => {
    // Check if a prompt is being edited
    if (editingPromptId) {
      // Get the current authenticated user
      const auth = getAuth();
      const user = auth.currentUser;
  
      // Ensure that the user is authenticated
      if (user) {
        const db = getFirestore();
        const promptRef = doc(db, `users/${user.uid}/prompts/${editingPromptId}`);
  
        try {
          // Update the Firestore document
          await updateDoc(promptRef, { content: editedText });
  
          // Update local promptsData state with the new edited text
          onUpdatePromptsData({ ...promptsData, [editingPromptId]: editedText });
  
          setSaveSuccess(true);
          setTimeout(() => setSaveSuccess(false), 6000);
          // Exit editing mode
          //setEditingPromptId(null);
          //setEditedText('');
        } catch (error) {
          console.error("Error updating prompt:", error);
        }
      } else {
        console.error("No authenticated user found");
      }
    }
  };

  const handleDelete = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (user && currentPromptId) {
      // SweetAlert confirmation dialog
      const willDelete = await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      });
  
      // Proceed only if confirmed
      if (willDelete.isConfirmed) {
        const db = getFirestore();
        const promptRef = doc(db, `users/${user.uid}/prompts/${currentPromptId}`);
        try {
          await deleteDoc(promptRef);
          handleMenuClose();
          console.log("Prompt deleted successfully");
  
          // Remove the deleted prompt from local state
          const updatedPromptsData = { ...promptsData };
          delete updatedPromptsData[currentPromptId];
          onUpdatePromptsData(updatedPromptsData);
  
          // Optionally, update selectedPrompts state if necessary
          const updatedSelectedPrompts = { ...selectedPrompts };
          delete updatedSelectedPrompts[currentPromptId];
          setSelectedPrompts(updatedSelectedPrompts);
          localStorage.setItem('selectedPrompts', JSON.stringify(updatedSelectedPrompts));
  
          refetchData();
        } catch (error) {
          console.error("Error deleting prompt:", error);
        }
      } else {
        // If the user clicks 'Cancel', you can log it or perform other actions
        console.log("Deletion cancelled");
      }
    } else {
      console.error("No authenticated user found or no prompt selected");
    }
  };
  

  const confirmAndDeletePrompt = async (promptId) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        deletePrompt(promptId);
      }
    });
  };







  const onStyle = {
    //background: 'linear-gradient(to left, #2152ff, #21d4fd) !important',
    padding: "10px",
    borderRadius: "12px",
    border: "1px solid #344767",
    boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)'
  };
  
  const offStyle = {
    padding: "10px",
    borderRadius: "12px",
    //border: "1px solid #e0e0e0",
    boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)'
  };
  
  const customCardStyle = {
    //backgroundColor: '#e9ecef',

    //boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.3)", 
  };

  return (
 
      <SoftBox pt={4.5} pb={3} px={7} lineHeight={1.25}>
        <Grid container spacing={2}>
          {Object.keys(promptsData).map((prompt, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Card sx={{ ...customCardStyle, ...(selectedPrompts[prompt] ? onStyle : offStyle) }}>
                <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                  <Switch
                    checked={selectedPrompts[prompt]}
                    onChange={() => handleSwitchChange(prompt)}
                  />
                  <SoftTypography py={1} variant="h6" fontWeight="medium" sx={{ flexGrow: 1, marginLeft: 3, color: selectedPrompts[prompt] ? 'info' : 'info' }}>
                    {prompt}
                  </SoftTypography>
                  <Icon
                    sx={{ cursor: "pointer", color: selectedPrompts[prompt] ? 'info !important' : 'info' }}
                    onClick={(e) => handleMenuClick(e, prompt)}
                  >
                    more_vert
                  </Icon>
                  <Menu
                    anchorEl={anchorEl?.[currentPromptId]}
                    open={Boolean(anchorEl?.[currentPromptId])}
                    onClose={handleMenuClose}
                    //sx={{ boxShadow: "none !important", border: "1px solid #344767" }}
                    PaperProps={{
                      style: {
                        boxShadow: '0px 0px 5px rgba(0, 0, 0, 0.01)', // Apply your desired boxShadow here
                      },
                    }}
                  >
                    <MenuItem onClick={handleEditClick}>
                      <ListItemIcon>
                        <EditOutlinedIcon fontSize="small" sx={{ color: '#344767' }} />
                      </ListItemIcon>
                      <ListItemText>
                        <SoftTypography color="dark" variant="button" fontWeight="medium">
                          Edit Prompt
                        </SoftTypography>
                      </ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleDelete}>
                      <ListItemIcon>
                        <DeleteOutlinedIcon fontSize="small" sx={{ color: "red" }} />
                      </ListItemIcon>
                      <ListItemText>
                        <SoftTypography color="error" variant="button" fontWeight="medium">
                          Delete
                        </SoftTypography>
                      </ListItemText>
                    </MenuItem>
                  </Menu>
                </SoftBox>
              </Card>
            </Grid>
          ))}

        </Grid>
        {editingPromptId && (
          <Card sx={{ marginTop: 4, padding: 2, boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)' }}>
              <SoftBox sx={{ padding: 1 }}>
                <SoftBox display="flex" alignItems="center" justifyContent="space-between">
                  <SoftBox display="flex" alignItems="center">
                    <SoftTypography variant="h6" fontWeight='medium' sx={{ ml: 1, mb: 1}}>Currently editing: </SoftTypography>
                    <SoftBadge
                      variant="contained"
                      color="warning"
                      size="sm"
                      sx={{ ml: 1, mb: 1}}
                      badgeContent={
                        <SoftTypography variant="button" fontWeight="medium" color="dark" >{editingPromptId} </SoftTypography>
                      }
                      //circular
                      //container
                    />
                  </SoftBox>
                  <SoftBox mb={1} >
                    <Tooltip title={subscription === "Silver Subscription" ? "🔔 Editing is available for Platinum and Gold Package subscribers only." : "Save your changes"}>
                        <span>
                            <SoftButton sx={{ mr: 1 }} color="dark" size="small" onClick={handleSave} disabled={subscription === "Silver Subscription"}>
                                Save
                            </SoftButton>
                        </span>
                    </Tooltip>

                    <Tooltip title="Closes the editing mode for this summary, unsaved changes will be lost" placement="top">
                    <SoftButton  color="dark" size="small" variant="outlined" onClick={handleCloseEdit} > 
                        <CloseIcon />
                    </SoftButton>
                    </Tooltip>
                  </SoftBox>
                </SoftBox>
                <SoftInput sx={{mb: 2, mt: 2}}
                    value={editedText}
                    onChange={(e) => subscription !== "Silver Subscription" && setEditedText(e.target.value)}
                    multiline
                    rows={15}
                    fullWidth
                />
                {subscription === "Silver Subscription" && (
                    <SoftBox px={4} py={2} display="flex" flexDirection="column" justifyContent="center" alignItems="center" >
                    <SoftBadge
                        variant="contained"
                        color="error"
                        size="md"
                        badgeContent={
                            <SoftTypography variant="button">
                                🔔&nbsp;&nbsp;Editing this summary is available for Platinum and Gold Package subscribers only.
                            </SoftTypography>
                        }
                        container
                    />
                    </SoftBox>
                )}
                <SoftBox display="flex" alignItems="center" justifyContent="space-between" >
                <SoftTypography pb={1} ml={1} pt={1} variant="body2" fontWeight="medium" sx={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={toggleHelp}  >
                    I need help writing the content
                </SoftTypography>
                {
                    saveSuccess && (
                        <SoftBadge
                            variant="contained"
                            color="success"
                            size="sm"
                            //sx={{ ml: 1 }}
                            badgeContent={
                                <SoftTypography fontWeight="medium" variant="body2">
                                    Changes saved successfully!
                                </SoftTypography>
                            }
                            //circular
                            //container
                        />
                    )
                }
                </SoftBox>
                { showHelp && (
                    <>
                    <SoftBox px={2} py={5} > 
                        < CustomTooltip toggleHelp={toggleHelp} />
                    </SoftBox>
                    </>

                )}
              </SoftBox>
          </Card>
        )}
      </SoftBox>

  );
}

PromptSettings.propTypes = {
  promptsData: PropTypes.objectOf(PropTypes.string).isRequired,
  onUpdatePromptsData: PropTypes.func.isRequired,
  refetchData: PropTypes.func,
  subscription: PropTypes.string
};

export default PromptSettings;
